import { useEffect, useState } from 'react';

export type LayoutMode = 'mobile' | 'desktop';

export type ViewportInfo = {
	mode: LayoutMode;
	isMobile: boolean;
	isDesktop: boolean;
	width: number;
	height: number;
	isLandscape: boolean;
	isPortrait: boolean;
};

/**
 * Hook to detect responsive layout mode and viewport information.
 * Uses matchMedia for responsive detection (client-side only).
 *
 * This hook provides comprehensive viewport information for responsive components,
 * including layout mode, dimensions, and orientation. It's designed to replace
 * simple boolean checks with more detailed layout information.
 *
 * @param mobileBreakpoint - Breakpoint below which layout is considered mobile (default: 768px)
 * @returns ViewportInfo object with detailed layout information
 *
 * @example
 * ```tsx
 * const viewport = useResponsiveLayout(); // Uses default breakpoint (768px)
 * const { mode, isMobile, isDesktop, width, height } = viewport;
 *
 * return (
 *   <div>
 *     {mode === 'mobile' ? <MobileLayout /> : <DesktopLayout />}
 *     <p>Viewport: {width}x{height}</p>
 *   </div>
 * );
 * ```
 *
 * @example
 * ```tsx
 * // Custom breakpoint
 * const viewport = useResponsiveLayout(1024); // Desktop at 1024px+
 *
 * return (
 *   <div>
 *     {viewport.mode === 'mobile' ? <MobileLayout /> : <DesktopLayout />}
 *   </div>
 * );
 * ```
 */
// Helper function to create default viewport info
const createDefaultViewportInfo = (): ViewportInfo => ({
	mode: 'desktop',
	isMobile: false,
	isDesktop: true,
	width: 1024,
	height: 768,
	isLandscape: true,
	isPortrait: false,
});

// Helper function to update viewport information
const updateViewportInfo = (mobileBreakpoint: number): ViewportInfo => {
	const width = window.innerWidth;
	const height = window.innerHeight;
	const isMobile = width < mobileBreakpoint;
	const isDesktop = width >= mobileBreakpoint;
	const isLandscape = width > height;
	const isPortrait = height > width;

	return {
		mode: isMobile ? 'mobile' : 'desktop',
		isMobile,
		isDesktop,
		width,
		height,
		isLandscape,
		isPortrait,
	};
};

// Helper function to add event listeners with fallback
const addMediaQueryListener = (query: MediaQueryList, handler: () => void) => {
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	if (query.addEventListener) {
		query.addEventListener('change', handler);
	} else {
		// Fallback for older browsers
		query.addListener(handler);
	}
};

// Helper function to remove event listeners with fallback
const removeMediaQueryListener = (query: MediaQueryList, handler: () => void) => {
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	if (query.removeEventListener) {
		query.removeEventListener('change', handler);
	} else {
		// Fallback for older browsers
		query.removeListener(handler);
	}
};

export const useResponsiveLayout = (mobileBreakpoint = 768): ViewportInfo => {
	const [viewportInfo, setViewportInfo] = useState<ViewportInfo>(createDefaultViewportInfo());

	useEffect(() => {
		// Function to update viewport information
		const handleViewportUpdate = () => {
			setViewportInfo(updateViewportInfo(mobileBreakpoint));
		};

		// Set initial values
		handleViewportUpdate();

		// Create media query for mobile breakpoint
		// Use max-width: 767px for 768px breakpoint to match width < 768px logic
		const mobileQuery = window.matchMedia(`(max-width: ${mobileBreakpoint - 1}px)`);
		const landscapeQuery = window.matchMedia('(orientation: landscape)');

		// Add event listeners
		addMediaQueryListener(mobileQuery, handleViewportUpdate);
		addMediaQueryListener(landscapeQuery, handleViewportUpdate);

		// Add resize listener for more accurate dimensions
		window.addEventListener('resize', handleViewportUpdate);

		// Cleanup function
		return () => {
			removeMediaQueryListener(mobileQuery, handleViewportUpdate);
			removeMediaQueryListener(landscapeQuery, handleViewportUpdate);
			window.removeEventListener('resize', handleViewportUpdate);
		};
	}, [mobileBreakpoint]);

	return viewportInfo;
};

/**
 * Convenience hook that returns just the layout mode.
 * This is a simpler alternative to useResponsiveLayout when you only need the mode.
 *
 * @param mobileBreakpoint - Breakpoint below which layout is considered mobile (default: 768px)
 * @returns LayoutMode ('mobile' | 'desktop')
 *
 * @example
 * ```tsx
 * const mode = useLayoutMode(); // Uses default breakpoint (768px)
 *
 * return (
 *   <div>
 *     {mode === 'mobile' ? <MobileLayout /> : <DesktopLayout />}
 *   </div>
 * );
 * ```
 */
export const useLayoutMode = (mobileBreakpoint = 768): LayoutMode => {
	const { mode } = useResponsiveLayout(mobileBreakpoint);
	return mode;
};

/**
 * Convenience hook that returns just the mobile/desktop boolean.
 * This maintains compatibility with the existing useIsMobile hook.
 *
 * @param mobileBreakpoint - Breakpoint below which layout is considered mobile (default: 768px)
 * @returns boolean indicating if the viewport is mobile
 *
 * @example
 * ```tsx
 * const isMobile = useIsMobile(); // Uses default breakpoint (768px)
 *
 * return (
 *   <div>
 *     {isMobile ? <MobileLayout /> : <DesktopLayout />}
 *   </div>
 * );
 * ```
 */
export const useIsMobile = (mobileBreakpoint = 768): boolean => {
	const { isMobile } = useResponsiveLayout(mobileBreakpoint);
	return isMobile;
};
